/** Job/role chips shown in a row's Job cell before the rest collapse into "+N". */
export const MAX_JOB_CHIPS = 2;

/**
 * How long (ms) the page waits for just-created rows to appear in its data before it stops waiting to reveal them.
 * They arrive a render after the save; this long means they never will (the page moved on), not that it's slow.
 */
export const PENDING_REVEAL_TIMEOUT_MS = 5000;

/** Floor for the height-filling grid block (toolbar + grid), so a short viewport scrolls the page rather than crushing the grid. */
export const GRID_MIN_HEIGHT = 320;

/**
 * The Location column's field: it holds the band's name block above it and, on a shift row, the On-demand tag. Not a
 * property of any line (`useBandLayout` reads the column's laid-out width by it).
 */
export const LOCATION_COLUMN_FIELD = "__location";

/**
 * The Location column's floor: a short name and the rename pencil in the band, "On-demand" on a row. It only binds on a
 * narrow grid; from about 1000px the column takes its share of the width (`COLUMN_FLEX`).
 */
export const LOCATION_COLUMN_MIN_WIDTH = 140;

/**
 * The columns' shares of the grid's width (MUI `flex`), in the design's proportions (its 1020px table: Location 236,
 * Job 104, Hours 176, Days 180, Target and Required 120 each), so at a 1360px grid every column stands where the
 * design draws it. A ratio department's ladder takes both figure columns' room and some of the Location column's, as
 * the design's ladder view does (Location 176, ladder 300). Each column still keeps its own floor.
 */
export const COLUMN_FLEX = {
	LOCATION: 2.36,
	LOCATION_RATIO: 1.76,
	JOB: 1.04,
	HOURS: 1.76,
	DAYS: 1.8,
	FIGURE: 1.2,
	LADDER: 3,
} as const;

/** The Job column's floor: two job chips, or "Registered Nurse". */
export const JOB_COLUMN_MIN_WIDTH = 130;

/** The Hours column's floor: the period glyph, "12:00 AM – 12:00 PM" and a short name. */
export const HOURS_COLUMN_MIN_WIDTH = 200;

/** The Hours column's floor while any row carries a Duplicate chip: the widest hours plus the chip, side by side. */
export const HOURS_WITH_MARK_MIN_WIDTH = 300;

/** The Days column's floor: a week of xsmall read-only day chips (152px) and the cell padding. */
export const DAYS_COLUMN_MIN_WIDTH = 172;

/**
 * The Target and Required columns' floor: a figure right-aligned under its header, or the inline stepper (76px) whose
 * value box lands where the figure stands, and the cell padding.
 */
export const FIGURE_COLUMN_MIN_WIDTH = 100;

/** The Ratio ladder column's floor: the widest rung chip ("21–∞ → 3"). */
export const LADDER_COLUMN_MIN_WIDTH = 160;

/** The trailing column holds one icon button (Remove, or a draft's Cancel) and the cell padding. */
export const ACTIONS_COLUMN_WIDTH = 56;

/** Rooms listed on an EMR area card before the rest collapse into "+N more". */
export const MAX_VARIANT_ROOM_CHIPS = 8;

/** Location names the "couldn't check for duplicates" banner lists before summarising the rest. */
export const MAX_NAMED_LOCATIONS = 3;

/** The EMR unit/floor dropdowns' "Not set" item: sent to the backend as `null`. */
export const EMR_FIELD_NOT_SET = "";

/**
 * The breakpoint (a laptop's width) from which the toolbar keeps one line: the search gives up width first, down to
 * `SEARCH_MIN_WIDTH`, and below `TOOLBAR_LABELLED_ACTIONS_FROM` the actions are icon buttons. Only content too wide
 * for even that (a long set of job chips) still wraps, moving Collapse all / New location to the next line as a unit
 * rather than overflowing. Below it the search keeps its full width and the filters wrap.
 */
export const TOOLBAR_ONE_LINE_FROM = "md";

/**
 * The breakpoint from which Collapse all, EMR locations and New location show their labels. Between
 * `TOOLBAR_ONE_LINE_FROM` and here they are icon buttons named by their tooltips: with "Clear filters" showing, their
 * labels (~400px) leave no room for the search on a 1024–1180px screen. Below `TOOLBAR_ONE_LINE_FROM` they get a line
 * of their own, so they keep their labels there.
 */
export const TOOLBAR_LABELLED_ACTIONS_FROM = "lg";

/** The toolbar's Job and Hours dropdowns' width (px). */
export const FACET_WIDTH = 168;

/** The search box's design width (px), which it keeps whenever the row has room. */
const SEARCH_WIDTH = 280;

/**
 * The narrowest the search box gets on a one-line toolbar (px): its glyph and padding take 56px, leaving 64px of text,
 * "Search" and a few more characters of its placeholder or a typed query.
 */
const SEARCH_MIN_WIDTH = 120;

/**
 * The search box's slot: a line of its own on a phone; beside the filters from `sm`, at its design width. From
 * `TOOLBAR_ONE_LINE_FROM` it is the one control that gives up width: the row lays out with it at `SEARCH_MIN_WIDTH`
 * (so the row wraps only when even that doesn't fit) and it grows back to its design width into whatever room is left.
 */
export const SEARCH_SLOT_SX = {
	flex: { xs: "1 1 100%", sm: `0 1 ${SEARCH_WIDTH}px`, [TOOLBAR_ONE_LINE_FROM]: `1 1 ${SEARCH_MIN_WIDTH}px` },
	minWidth: { sm: SEARCH_MIN_WIDTH },
	maxWidth: { [TOOLBAR_ONE_LINE_FROM]: SEARCH_WIDTH },
} as const;

/**
 * How long (ms) the "rows sharing this shift's staff weren't updated" snackbar stays up. Longer than a confirmation's:
 * it carries the Retry, and those rows are short of staff until it's pressed.
 */
export const SIBLINGS_RETRY_SNACKBAR_MS = 20000;

/**
 * How long (ms) a Locations-page edit waits after its last change before it saves, so a run of changes is one save.
 * One value for the page, so its two edit-as-you-go surfaces feel the same:
 * - an inline row: each Target/Required or day click restarts it (steppers and days alike); an hours change (the
 *   picker's Apply) saves at once, taking any waiting change with it;
 * - the census-area popover: each unit or floor pick, and the rooms menu closing, restarts it; closing the popover
 *   saves at once (`useDebouncedEmrSave`).
 */
export const INLINE_SAVE_DEBOUNCE_MS = 600;

/**
 * How long (ms) after its last save lands a row keeps its staffing lines as they were drawn while the admin edited
 * them. Only then (with nothing waiting and focus outside the row) do lines that now share a target merge, so a line
 * never jumps away under the pointer mid-edit.
 */
export const INLINE_REGROUP_IDLE_MS = 1500;
